"use client"

import { useState, useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { apiService } from "../services/apiService"
import "./myScans.css"
import Layout from "../Layout/Layout"

const MyScans = () => {
  const [scans, setScans] = useState([])
  const [filterStatus, setFilterStatus] = useState("All")
  const [loading, setLoading] = useState(true)
  const toast = useRef(null)
  const navigate = useNavigate()
  const { email } = useSelector((state) => state.user)

  useEffect(() => {
    const fetchRecentAnalyses = async () => {
      try {
        setLoading(true)
        const res = await apiService.getRecentAnalyses(email)
        console.log("Recent analyses:", res)
        const formattedScans = (res.body || []).map((item) => ({
          id: item._id || Math.random().toString(36).substr(2, 9),
          file: item.fileUrl.split("/").pop(),
          status: item.analysis.error || item.analysis.html?.includes("error") ? "Error" : "Complete",
          date: new Date(item.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
          fullDate: new Date(item.createdAt),
          size: "2.4 MB", // Mock file size - replace with actual if available
          fullData: item,
        }))
        setScans(formattedScans.sort((a, b) => b.fullDate - a.fullDate))
      } catch (err) {
        console.error("Failed to fetch recent analyses", err)
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to fetch recent analyses",
          })
        }
      } finally {
        setLoading(false)
      }
    }
    if (email) fetchRecentAnalyses()
  }, [email])

  const getStatusBadge = (status) => {
    const statusConfig = {
      Complete: { class: "status-complete", icon: "✓" },
      Error: { class: "status-error", icon: "✕" },
      Processing: { class: "status-processing", icon: "⟳" },
    }

    const config = statusConfig[status] || statusConfig.Processing
    return (
      <span className={`status-badge ${config.class}`}>
        <span className="status-indicator">{config.icon}</span>
        {status}
      </span>
    )
  }

  const filteredScans = scans.filter((item) => filterStatus === "All" || item.status === filterStatus)

  const handleViewAnalysis = (scanData) => {
    navigate("/analysis", { state: { data: scanData.fullData } })
  }

  const handleStartNewScan = () => {
    navigate("/landing")
  }

  const ScanCard = ({ scan }) => (
    <div className="scan-card">
      <div className="scan-card-header">
        <div className="file-info">
          <div className="file-icon">
            <span style={{ color: "white", fontSize: "1.2rem" }}>📄</span>
          </div>
          <div className="file-name">{scan.file}</div>
          <div className="file-size">{scan.size}</div>
        </div>
        {getStatusBadge(scan.status)}
      </div>

      <div className="scan-details">
        <div className="detail-row">
          <span className="detail-label">Analysis Date</span>
          <span className="detail-value">{scan.date}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Document Type</span>
          <span className="detail-value">PDF Contract</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Processing Time</span>
          <span className="detail-value">~2 minutes</span>
        </div>
      </div>

      <div className="card-actions">
        <button
          className="action-button action-primary"
          onClick={() => handleViewAnalysis(scan)}
          disabled={scan.status === "Error"}
        >
          <span>👁️</span>
          View Analysis
        </button>
        <button className="action-button action-secondary">
          <span>📥</span>
          Download
        </button>
      </div>
    </div>
  )

  const EmptyState = () => (
    <div className="empty-state">
      <div className="empty-icon">📊</div>
      <h3 className="empty-title">No Scans Yet</h3>
      <p className="empty-description">
        Start analyzing your first contract to see detailed insights and risk assessments.
      </p>
      <button className="professional-button button-primary" onClick={handleStartNewScan}>
        <span>🚀</span>
        Start Your First Scan
      </button>
    </div>
  )

  const LoadingState = () => (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p className="loading-text">Loading your scans...</p>
    </div>
  )

  if (loading) {
    return (
      <Layout>
        <div className="my-scans-container">
          <LoadingState />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="my-scans-container">
        {/* Professional Header */}
        <div className="scans-header">
          <div className="scans-header-content">
            <div>
              <h1 className="scans-title">My Contract Scans</h1>
              <p className="scans-subtitle">Manage and review your contract analysis history</p>
            </div>
            <div className="header-actions">
              <button className="professional-button button-primary" onClick={handleStartNewScan}>
                <span>➕</span>
                Start New Scan
              </button>
              <button className="professional-button button-secondary">
                <span>📊</span>
                Export Report
              </button>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="filter-section">
          <div className="filter-content">
            <div className="filter-info">
              <div className="scan-count">
                Total Scans: <span className="scan-count-number">{scans.length}</span>
              </div>
              <div className="scan-count">
                Filtered: <span className="scan-count-number">{filteredScans.length}</span>
              </div>
            </div>
            <div className="filter-controls">
              <div className="filter-dropdown">
                <select
                  className="filter-select"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="All">All Status</option>
                  <option value="Complete">Complete</option>
                  <option value="Error">Error</option>
                  <option value="Processing">Processing</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Scans Content */}
        {filteredScans.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="scans-grid">
            {filteredScans.map((scan) => (
              <ScanCard key={scan.id} scan={scan} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}

export default MyScans
